import { useCallback } from 'react';

/**
 * Pure index math for the WAI-ARIA roving-tabindex tablist pattern.
 *
 * Given the key pressed, the current active index and the tab count,
 * returns the next active index — or `null` when the key is not a
 * tablist navigation key (caller should ignore it). Wraps around at the
 * ends for Arrow keys; Home/End jump to the extremes.
 *
 * Extracted as a standalone export so both IndustryTabs and the PDP
 * tab strip (issue #143) share one keyboard contract instead of
 * duplicating arrow-key handling.
 *
 * @param key - KeyboardEvent.key value
 * @param current - currently active tab index
 * @param count - number of tabs
 * @param orientation - which arrow axis advances the tablist. `'both'`
 *   responds to both axes — appropriate for a responsive tablist that
 *   lays out horizontally at one breakpoint and vertically at another.
 */
export type RovingOrientation = 'horizontal' | 'vertical' | 'both';

export function nextRovingIndex(
  key: string,
  current: number,
  count: number,
  orientation: RovingOrientation = 'horizontal',
): number | null {
  if (count <= 0) return null;
  const forwardKeys =
    orientation === 'vertical'
      ? ['ArrowDown']
      : orientation === 'both'
        ? ['ArrowRight', 'ArrowDown']
        : ['ArrowRight'];
  const backwardKeys =
    orientation === 'vertical'
      ? ['ArrowUp']
      : orientation === 'both'
        ? ['ArrowLeft', 'ArrowUp']
        : ['ArrowLeft'];

  if (forwardKeys.includes(key)) return (current + 1) % count;
  if (backwardKeys.includes(key)) return (current - 1 + count) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

interface UseRovingTabIndexResult {
  /** `onKeyDown` to spread onto each tab button. */
  onKeyDown: (event: React.KeyboardEvent) => void;
  /** tabIndex for the tab at `index` — 0 when active, -1 otherwise. */
  getTabIndex: (index: number) => 0 | -1;
}

/**
 * React wrapper around {@link nextRovingIndex}. Moves the active tab on
 * Arrow / Home / End, calls `onChange` with the new index, and focuses
 * the newly-active tab so keyboard focus follows selection (the
 * "selection follows focus" tablist variant — appropriate here because
 * activating a tab only swaps an in-page panel, no navigation cost).
 *
 * @param activeIndex - currently active tab index
 * @param count - number of tabs
 * @param onChange - called with the next index when a nav key is pressed
 * @param orientation - arrow-key axis (default horizontal)
 */
export function useRovingTabIndex(
  activeIndex: number,
  count: number,
  onChange: (index: number) => void,
  orientation: RovingOrientation = 'horizontal',
): UseRovingTabIndexResult {
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const next = nextRovingIndex(event.key, activeIndex, count, orientation);
      if (next === null) return;
      event.preventDefault();
      onChange(next);
      // Move focus to the newly-active tab within the same tablist.
      const tablist = (event.currentTarget as HTMLElement).closest('[role="tablist"]');
      const tabs = tablist?.querySelectorAll<HTMLElement>('[role="tab"]');
      tabs?.[next]?.focus();
    },
    [activeIndex, count, onChange, orientation],
  );

  const getTabIndex = useCallback(
    (index: number): 0 | -1 => (index === activeIndex ? 0 : -1),
    [activeIndex],
  );

  return { onKeyDown, getTabIndex };
}
